/* The backend owns identity checks, customer sync and entry receipt.
 * The separate local preview uses a native POST and never calls Shopify. */
if (!customElements.get('pn-lucky-draw')) {
  customElements.define('pn-lucky-draw', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      const form = this.querySelector('form');
      if (!form) return;
      if (form.querySelector('[data-raffle-unavailable]')) return;
      const apiMode = form.dataset.raffleMode === 'api';
      // Fragments are not sent to Shopify or included in HTTP referrers.
      this.accessToken = new URLSearchParams(window.location.hash.slice(1)).get('invite') || '';

      const source = this.querySelector('[data-raffle-source]');
      if (source) {
        const params = new URLSearchParams(window.location.search);
        source.value = this.accessToken || params.get('utm_medium') === 'qr' ? 'Showroom QR' : 'Website';
      }

      const phone = form.querySelector('input[type="tel"]');
      const name = form.querySelector('input[autocomplete="name"]');
      const validate = () => {
        if (name) name.setCustomValidity(name.value && !name.value.trim() ? 'Please enter your name.' : '');
        if (phone) {
          const digits = phone.value.replace(/\D/g, '');
          const isPhone = /^[+\d\s().-]+$/.test(phone.value) && digits.length >= 10 && digits.length <= 17;
          phone.setCustomValidity(phone.value && !isPhone ? 'Please enter a valid phone number with area code.' : '');
        }
        const complete = Array.from(form.querySelectorAll('input[required]')).every(input => input.validity.valid);
        this.querySelector('[data-raffle-step="details"]')?.classList.toggle('is-current', !complete);
        this.querySelector('[data-raffle-step="ready"]')?.classList.toggle('is-current', complete);
      };
      form.addEventListener('input', validate, { signal });
      validate();

      const submit = form.querySelector('button[type="submit"]');
      const status = this.querySelector('[data-raffle-status]');
      const resetSubmit = () => {
        if (submit) submit.disabled = apiMode && !this.verificationToken;
        form.removeAttribute('aria-busy');
        if (status) status.textContent = '';
        clearTimeout(this.submitTimer);
      };
      window.addEventListener('pageshow', event => {
        if (!apiMode) return resetSubmit();
        if (event.persisted && this.widgetId !== undefined) {
          this.verificationToken = '';
          resetSubmit();
          window.turnstile?.reset(this.widgetId);
        }
      }, { signal });
      form.addEventListener('submit', async event => {
        if (apiMode) event.preventDefault();
        validate();
        if (!form.reportValidity()) {
          event.preventDefault();
          return;
        }
        if (form.getAttribute('aria-busy') === 'true') { event.preventDefault(); return; }
        if (apiMode && !this.verificationToken) {
          status.textContent = 'Please complete the verification before entering.';
          return;
        }
        if (submit) submit.disabled = true;
        form.setAttribute('aria-busy', 'true');
        if (status) status.textContent = 'Sending your entry…';
        if (!apiMode) { this.submitTimer = setTimeout(resetSubmit, 15000); return; }
        const fields = new FormData(form);
        try {
          const endpoint = new URL(form.dataset.raffleApi);
          if (endpoint.protocol !== 'https:') throw new Error('configuration');
          const response = await fetch(`${endpoint.href.replace(/\/$/, '')}/${encodeURIComponent(form.dataset.raffleCampaign)}/entries`, {
            method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' },
            signal: AbortSignal.timeout(120000),
            body: JSON.stringify({ name: fields.get('name'), phone: fields.get('phone'),
              email: fields.get('email'), zip: fields.get('zip'),
              source: fields.get('source'), token: this.verificationToken,
              receiptToken: this.getReceiptToken(form.dataset.raffleCampaign),
              preferences: fields.getAll('preferences') }),
          });
          const result = await response.json();
          if (!response.ok || !['received', 'duplicate'].includes(result.status)) {
            const messages = {
              RAFFLE_NOT_CONFIGURED: 'Entries are temporarily unavailable. Please ask the Pacific Nook team at the booth for help.',
              ENTRIES_CLOSED: 'Entries are closed. Thank you for your interest.',
              INVALID_INPUT: 'Please check your contact details and include your phone area or country code.',
              QR_ACCESS_REQUIRED: 'This invitation is no longer valid. Please scan the current showroom QR code.',
              TOO_MANY_REQUESTS: 'Too many attempts. Please wait a few minutes before trying again.',
              VERIFICATION_FAILED: 'Please complete the verification again and try once more.',
            };
            throw new Error(messages[result.error] || 'We could not confirm your entry. Please try again with the same email and phone.');
          }
          if (result.draw?.status === 'pending') {
            status.textContent = 'Your entry is saved. We’re still verifying your details. Try again with the same details to check your result, or ask our team for help. You will not draw twice.';
            this.readyLabel = 'Check my result';
            submit.querySelector('span').textContent = this.readyLabel;
            return;
          }
          if (result.draw && !['won', 'not_selected'].includes(result.draw.status)) throw new Error('We could not confirm your result. Please try again with the same details.');
          if (this.widgetId !== undefined) window.turnstile?.remove(this.widgetId);
          this.widgetId = undefined;
          form.replaceChildren(this.querySelector('[data-raffle-success-template]').content.cloneNode(true));
          if (result.status === 'duplicate') {
            form.querySelector('.pn-raffle__overline').textContent = 'ALREADY ENTERED';
            form.querySelector('h2').textContent = 'You’re already in.';
            form.querySelector('h2 + p').textContent = 'An entry already uses this email or phone. No additional entry was created. Use your original browser and details to check your result, or ask our team for help.';
            form.querySelector('.pn-raffle__entry-ticket').hidden = true;
            form.querySelector('[data-raffle-success]').dataset.celebrate = 'false';
          } else if (result.draw?.status === 'won') {
            const prizeNames = { first: 'FIRST PRIZE', second: 'SECOND PRIZE', third: 'THIRD PRIZE' };
            form.querySelector('.pn-raffle__overline').textContent = 'YOU WON';
            form.querySelector('h2').textContent = 'A little luck, just for you.';
            form.querySelector('h2 + p').textContent = result.draw.delivery === 'emailed'
              ? 'Your gift card email is on its way. Check your inbox and spam folder.'
              : 'Your prize is reserved. Your gift card email is pending; our team can help if it doesn’t arrive. You do not need to enter again.';
            const ticket = form.querySelector('.pn-raffle__entry-ticket');
            ticket.querySelector('b').textContent = `$${result.draw.amountCents / 100} Gift Card`;
            ticket.querySelector('strong').textContent = prizeNames[result.draw.prizeId];
            ticket.querySelector('b + span').textContent = 'YOUR LUCKY NOOK PRIZE';
            ticket.querySelector('small').textContent = 'One lovely gift for your home.';
          } else if (result.draw?.status === 'not_selected') {
            form.querySelector('.pn-raffle__overline').textContent = 'THANK YOU FOR JOINING';
            form.querySelector('h2').textContent = 'No prize this time.';
            form.querySelector('h2 + p').textContent = 'Thank you for visiting Pacific Nook. We hope you find something lovely for your home. Your one-time draw is complete.';
            form.querySelector('.pn-raffle__entry-ticket').hidden = true;
            form.querySelector('[data-raffle-success]').dataset.celebrate = 'false';
          }
          form.querySelector('[data-raffle-focus]')?.focus();
          this.setupSuccess();
        } catch (error) {
          status.textContent = error.message === 'configuration' ? 'Entries are temporarily unavailable. Please ask the Pacific Nook team at the booth for help.' :
            error.name === 'TimeoutError' || error.name === 'TypeError' ? 'We could not confirm your entry. Please try again with the same email and phone.' : error.message;
        } finally {
          this.verificationToken = '';
          form.removeAttribute('aria-busy');
          if (this.widgetId !== undefined) window.turnstile?.reset(this.widgetId);
        }
      }, { signal });

      if (apiMode && submit) this.setupVerification(form, submit, status);

      this.querySelector('[data-raffle-focus]')?.focus();
      this.setupSuccess();
    }

    getReceiptToken(campaignId) {
      if (this.receiptToken) return this.receiptToken;
      const key = `pn-raffle-receipt:${campaignId}`;
      try { this.receiptToken = sessionStorage.getItem(key); } catch { /* Storage can be unavailable. */ }
      if (!/^[a-f0-9]{64}$/.test(this.receiptToken || '')) {
        this.receiptToken = Array.from(crypto.getRandomValues(new Uint8Array(32)), byte => byte.toString(16).padStart(2, '0')).join('');
        try { sessionStorage.setItem(key, this.receiptToken); } catch { /* Keep the in-memory receipt for retries. */ }
      }
      return this.receiptToken;
    }

    setupSuccess() {
      const success = this.querySelector('[data-raffle-success]');
      if (success && success.dataset.celebrate !== 'false') {
        this.celebrate();
        const replay = this.querySelector('[data-raffle-celebrate]');
        if (replay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          replay.hidden = false;
          replay.addEventListener('click', () => this.celebrate(), { signal: this.controller.signal });
        }
      }
    }

    async setupVerification(form, submit, status) {
      const label = submit.querySelector('span');
      if (!form.dataset.raffleApi || !form.dataset.raffleCampaign || !form.dataset.raffleSitekey) {
        label.textContent = 'Entries unavailable';
        status.textContent = 'Entries are temporarily unavailable. Please ask the Pacific Nook team at the booth for help.';
        return;
      }
      try {
        if (!window.pnRaffleTurnstile) {
          window.pnRaffleTurnstile = new Promise((resolve, reject) => {
            if (window.turnstile) return resolve();
            const script = document.createElement('script');
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
            script.async = true;
            script.onload = resolve;
            script.onerror = reject;
            document.head.append(script);
          });
        }
        await window.pnRaffleTurnstile;
        if (!this.isConnected) return;
        this.widgetId = window.turnstile.render(form.querySelector('[data-raffle-verification]'), {
          sitekey: form.dataset.raffleSitekey, action: 'raffle_entry', theme: 'light', size: 'flexible',
          callback: token => { this.verificationToken = token; label.textContent = this.readyLabel || 'Enter the draw'; submit.disabled = form.getAttribute('aria-busy') === 'true'; if (this.verificationFailed) { status.textContent = ''; this.verificationFailed = false; } },
          'expired-callback': () => { this.verificationToken = ''; submit.disabled = true; label.textContent = 'Preparing verification…'; },
          'error-callback': () => { this.verificationToken = ''; submit.disabled = true; this.verificationFailed = true; label.textContent = 'Verification unavailable'; status.textContent = 'Verification could not load. Please refresh and try again.'; },
        });
      } catch {
        label.textContent = 'Verification unavailable';
        status.textContent = 'Verification could not load. Please refresh and try again.';
      }
    }

    celebrate() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const container = this.querySelector('[data-raffle-confetti]');
      if (!container) return;
      clearTimeout(this.confettiTimer);
      const colors = ['#b9955a', '#8c9a7a', '#645143', '#d9c6a0'];
      const pieces = Array.from({ length: 32 }, (_, index) => {
        const piece = document.createElement('i');
        piece.style.setProperty('--x', `${Math.random() * 100}%`);
        piece.style.setProperty('--delay', `${Math.random() * 0.7}s`);
        piece.style.setProperty('--duration', `${2 + Math.random()}s`);
        piece.style.setProperty('--rotation', `${Math.random() * 600 - 300}deg`);
        piece.style.setProperty('--piece-color', colors[index % colors.length]);
        return piece;
      });
      container.replaceChildren(...pieces);
      this.confettiTimer = setTimeout(() => container.replaceChildren(), 4000);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      if (this.widgetId !== undefined) window.turnstile?.remove(this.widgetId);
      this.widgetId = undefined;
      this.verificationToken = '';
      this.accessToken = '';
      clearTimeout(this.submitTimer);
      clearTimeout(this.confettiTimer);
    }
  });
}
